import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import './TipTapEditor.css';

// TipTap Toolbar Component
const TipTapToolbar = ({ editor, isDark, onImageUpload, uploadingImages }) => {
  if (!editor) {
    return null;
  }

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const insertTable = () => {
    // Ask for rows
    const rowsInput = window.prompt('Enter number of rows:', '3');
    if (rowsInput === null) return; // User cancelled
    
    const rows = parseInt(rowsInput);
    if (isNaN(rows) || rows < 1 || rows > 20) {
      alert('Please enter a valid number between 1 and 20 for rows');
      return;
    }

    // Ask for columns
    const colsInput = window.prompt('Enter number of columns:', '3');
    if (colsInput === null) return; // User cancelled
    
    const cols = parseInt(colsInput);
    if (isNaN(cols) || cols < 1 || cols > 20) {
      alert('Please enter a valid number between 1 and 20 for columns');
      return;
    }

    // Insert table with user specified dimensions
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
  };

  return (
    <div className={`tiptap-toolbar ${isDark ? 'tiptap-toolbar-dark' : ''}`}>
      {/* Text Style Dropdown */}
      <select
        value={editor.getAttributes('heading').level || 'paragraph'}
        onChange={(e) => {
          const value = e.target.value;
          if (value === 'paragraph') {
            editor.chain().focus().setParagraph().run();
          } else {
            editor.chain().focus().toggleHeading({ level: parseInt(value) }).run();
          }
        }}
        className={`tiptap-select ${isDark ? 'tiptap-select-dark' : ''}`}
        title="Text Style - Choose heading level or normal text"
      >
        <option value="paragraph">Normal</option>
        <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="3">Heading 3</option>
        <option value="4">Heading 4</option>
        <option value="5">Heading 5</option>
        <option value="6">Heading 6</option>
      </select>

      <div className="tiptap-toolbar-divider"></div>

      {/* Text Formatting */}
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        className={`tiptap-button ${editor.isActive('bold') ? 'is-active' : ''} ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Bold (Ctrl+B)"
      >
        <strong>B</strong>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        className={`tiptap-button ${editor.isActive('italic') ? 'is-active' : ''} ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Italic (Ctrl+I)"
      >
        <em>I</em>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={`tiptap-button ${editor.isActive('underline') ? 'is-active' : ''} ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Underline"
      >
        <u>U</u>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={!editor.can().chain().focus().toggleStrike().run()}
        className={`tiptap-button ${editor.isActive('strike') ? 'is-active' : ''} ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Strikethrough"
      >
        <s>S</s>
      </button>

      <div className="tiptap-toolbar-divider"></div>

      {/* Lists */}
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`tiptap-button ${editor.isActive('bulletList') ? 'is-active' : ''} ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Bullet List - Create unordered list with bullet points"
      >
        •
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`tiptap-button ${editor.isActive('orderedList') ? 'is-active' : ''} ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Numbered List - Create ordered list with numbers"
      >
        1.
      </button>

      <div className="tiptap-toolbar-divider"></div>

      {/* Script */}
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleSubscript().run()}
        className={`tiptap-button ${editor.isActive('subscript') ? 'is-active' : ''} ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Subscript"
      >
        X₂
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleSuperscript().run()}
        className={`tiptap-button ${editor.isActive('superscript') ? 'is-active' : ''} ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Superscript"
      >
        X²
      </button>

      <div className="tiptap-toolbar-divider"></div>

      {/* Indent */}
      <button
        type="button"
        onClick={() => editor.chain().focus().liftListItem('listItem').run()}
        disabled={!editor.can().chain().focus().liftListItem('listItem').run()}
        className={`tiptap-button ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Decrease Indent - Move list item left"
      >
        ←
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().sinkListItem('listItem').run()}
        disabled={!editor.can().chain().focus().sinkListItem('listItem').run()}
        className={`tiptap-button ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Increase Indent - Move list item right"
      >
        →
      </button>

      <div className="tiptap-toolbar-divider"></div>

      {/* Color */}
      <input
        type="color"
        onInput={(e) => editor.chain().focus().setColor(e.target.value).run()}
        value={editor.getAttributes('textStyle').color || (isDark ? '#ffffff' : '#000000')}
        className="tiptap-color-input"
        title="Text Color - Change text color"
      />

      <div className="tiptap-toolbar-divider"></div>

      {/* Alignment */}
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        className={`tiptap-button ${editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''} ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Align Left"
      >
        ⬅
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        className={`tiptap-button ${editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''} ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Align Center"
      >
        ⬌
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        className={`tiptap-button ${editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''} ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Align Right"
      >
        ➡
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
        className={`tiptap-button ${editor.isActive({ textAlign: 'justify' }) ? 'is-active' : ''} ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Justify"
      >
        ⬌⬌
      </button>

      <div className="tiptap-toolbar-divider"></div>

      {/* Link, Image */}
      <button
        type="button"
        onClick={setLink}
        className={`tiptap-button ${editor.isActive('link') ? 'is-active' : ''} ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Insert Link - Add hyperlink to selected text"
      >
        🔗
      </button>
      <button
        type="button"
        onClick={onImageUpload}
        disabled={uploadingImages}
        className={`tiptap-button ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Insert Image - Upload and insert image"
      >
        {uploadingImages ? '⏳' : '🖼️'}
      </button>

      <div className="tiptap-toolbar-divider"></div>

      {/* Table */}
      <button
        type="button"
        onClick={insertTable}
        className={`tiptap-button ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Insert Table - Add a table to your content"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 2C0 0.895431 0.895431 0 2 0H14C15.1046 0 16 0.895431 16 2V14C16 15.1046 15.1046 16 14 16H2C0.895431 16 0 15.1046 0 14V2Z" fill="currentColor" fillOpacity="0.1"/>
          <path d="M5 0V16M11 0V16M0 5H16M0 11H16" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      </button>
      {editor.isActive('table') && (
        <>
          <button
            type="button"
            onClick={() => editor.chain().focus().addRowBefore().run()}
            className={`tiptap-button ${isDark ? 'tiptap-button-dark' : ''}`}
            title="Add Row Above"
          >
            ⬆
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().addRowAfter().run()}
            className={`tiptap-button ${isDark ? 'tiptap-button-dark' : ''}`}
            title="Add Row Below"
          >
            ⬇
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().addColumnBefore().run()}
            className={`tiptap-button ${isDark ? 'tiptap-button-dark' : ''}`}
            title="Add Column Left"
          >
            ⬅
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().addColumnAfter().run()}
            className={`tiptap-button ${isDark ? 'tiptap-button-dark' : ''}`}
            title="Add Column Right"
          >
            ➡
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().deleteRow().run()}
            className={`tiptap-button ${isDark ? 'tiptap-button-dark' : ''}`}
            title="Delete Row"
          >
            🗑️
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().deleteColumn().run()}
            className={`tiptap-button ${isDark ? 'tiptap-button-dark' : ''}`}
            title="Delete Column"
          >
            🗑️
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().deleteTable().run()}
            className={`tiptap-button ${isDark ? 'tiptap-button-dark' : ''}`}
            title="Delete Table"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 2C0 0.895431 0.895431 0 2 0H14C15.1046 0 16 0.895431 16 2V14C16 15.1046 15.1046 16 14 16H2C0.895431 16 0 15.1046 0 14V2Z" fill="currentColor" fillOpacity="0.1"/>
              <path d="M5 0V16M11 0V16M0 5H16M0 11H16" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </>
      )}

      <div className="tiptap-toolbar-divider"></div>

      {/* Blockquote, Code */}
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={`tiptap-button ${editor.isActive('blockquote') ? 'is-active' : ''} ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Blockquote - Format text as a quote"
      >
        "
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={`tiptap-button ${editor.isActive('codeBlock') ? 'is-active' : ''} ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Code Block - Insert code snippet"
      >
        &lt;/&gt;
      </button>

      <div className="tiptap-toolbar-divider"></div>

      {/* Clear Formatting */}
      <button
        type="button"
        onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
        className={`tiptap-button ${isDark ? 'tiptap-button-dark' : ''}`}
        title="Clear Formatting"
      >
        🧹
      </button>
    </div>
  );
};

TipTapToolbar.displayName = 'TipTapToolbar';

// TipTap Editor Component
const TipTapEditor = React.memo(({ 
  value, 
  onChange, 
  placeholder, 
  isDark, 
  onImageUpload,
  uploadingImages,
  editorRef
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        blockquote: {
          HTMLAttributes: {
            class: 'tiptap-blockquote',
          },
        },
        codeBlock: {
          HTMLAttributes: {
            class: 'tiptap-code-block',
          },
        },
      }),
      Image.configure({
        inline: true,
        allowBase64: false,
        HTMLAttributes: {
          class: 'tiptap-image',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'tiptap-link',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Color,
      TextStyle,
      Underline,
      Strike,
      Subscript,
      Superscript,
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'tiptap-table',
        },
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
    editorProps: {
      attributes: {
        class: `tiptap-editor ${isDark ? 'tiptap-dark' : ''}`,
        'data-placeholder': placeholder || 'Start writing your blog content here...',
      },
    },
  });

  // Expose editor instance via ref
  useEffect(() => {
    if (editorRef && editor) {
      editorRef.current = editor;
    }
  }, [editor, editorRef]);

  // Update editor content when value prop changes externally
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className={`tiptap-wrapper ${isDark ? 'tiptap-dark' : ''}`}>
      <TipTapToolbar 
        editor={editor} 
        isDark={isDark}
        onImageUpload={onImageUpload}
        uploadingImages={uploadingImages}
      />
      <EditorContent editor={editor} />
    </div>
  );
});

TipTapEditor.displayName = 'TipTapEditor';

export default TipTapEditor;
